import React from "react";
import { Column } from "@/components/Datatable/types";
import { EmailNotification } from "@/api/fetchMyEmailNotifications/types";
import { CheckCircle, XCircle } from "lucide-react";
import MyEmailNotificationActionMenu from "@/features/myEmailNotifications/components/MyEmailNotificationActionMenu/MyEmailNotificationActionMenu";

/**
 * Helper to format date/time into local user's system time zone
 */
function formatDateTime(dateString?: string | null): string {
  if (!dateString) return "--";
  const dateObj = new Date(dateString);
  if (isNaN(dateObj.getTime())) return "--"; // invalid date

  // Format with options to exclude seconds
  return dateObj.toLocaleString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * A reusable status badge component for boolean values
 */
interface StatusBadgeProps {
  value: boolean;
  positiveText?: string;
  negativeText?: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({
  value,
  positiveText = "Yes",
  negativeText = "No",
}) => {
  if (value) {
    return (
      <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-3.5 h-3.5 mr-1" />
        {positiveText}
      </div>
    );
  }

  return (
    <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
      <XCircle className="w-3.5 h-3.5 mr-1" />
      {negativeText}
    </div>
  );
};

/**
 * Props for the columns generator
 */
interface CreateMyEmailNotificationsColumnsProps {
  onAskPreview: (notification: EmailNotification) => void;
}

/**
 * We build the columns with a simpler view focused on the user's own notifications
 */
export function createMyEmailNotificationsColumns(
  props: CreateMyEmailNotificationsColumnsProps,
): Column<EmailNotification>[] {
  return [
    {
      header: "Subject",
      accessorKey: "subject",
    },
    {
      header: "Chapter",
      accessorKey: "chapterPermanentName",
      cell: ({ row }) => row.original.chapterPermanentName || "--",
    },
    {
      header: "Approved",
      accessorKey: "approved",
      cell: ({ row }) => <StatusBadge value={Boolean(row.original.approved)} />,
    },
    {
      header: "Sent",
      accessorKey: "sent",
      cell: ({ row }) => <StatusBadge value={Boolean(row.original.sent)} />,
    },
    {
      header: "Created At",
      accessorKey: "createdAt",
      cell: ({ row }) => formatDateTime(row.original.createdAt),
    },
    {
      header: "Actions",
      id: "actions",
      cell: ({ row }) => {
        const notification = row.original;
        return (
          <MyEmailNotificationActionMenu
            notification={notification}
            onPreview={props.onAskPreview}
          />
        );
      },
    },
  ];
}
